import axios from "axios";
import { toast } from "sonner";
import { API } from "@/config/constants";

const QUEUE_KEY = "yems_intervention_queue";
const MAX_QUEUE_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Offline queue helpers
// ---------------------------------------------------------------------------
function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Drop entries older than 24h (likely no-longer-relevant)
    const now = Date.now();
    return parsed.filter((q) => now - (q.queuedAt || 0) < MAX_QUEUE_AGE_MS);
  } catch {
    return [];
  }
}

function saveQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    /* noop */
  }
}

function enqueue(entry) {
  const q = loadQueue();
  q.push({ ...entry, queuedAt: Date.now() });
  saveQueue(q);
}

/**
 * Attempt to flush queued entries. Called on every successful log AND on
 * network reconnect / app startup.
 */
async function drainQueue() {
  const queue = loadQueue();
  if (queue.length === 0) return { sent: 0, failed: 0 };

  let sent = 0;
  let failed = 0;
  const remaining = [];

  // Find or create active log first
  let log;
  try {
    const res = await axios.get(`${API}/intervention-logs?status=active`);
    log = res.data?.[0];
    if (!log) {
      const created = await axios.post(`${API}/intervention-logs`, {
        patient_label: `Trip ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
      });
      log = created.data;
    }
  } catch {
    return { sent: 0, failed: queue.length };
  }

  for (const item of queue) {
    try {
      await axios.post(`${API}/intervention-logs/${log.id}/entries`, item.payload);
      sent++;
    } catch {
      remaining.push(item);
      failed++;
    }
  }
  saveQueue(remaining);
  return { sent, failed };
}

// Auto-drain on network reconnect
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    drainQueue().then(({ sent }) => {
      if (sent > 0) toast.success(`Synced ${sent} queued intervention${sent !== 1 ? "s" : ""}`);
    });
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Append an entry to the active intervention log.
 * If no active log exists, automatically starts one labelled with the current time.
 *
 * @param {Object} entry - { type, name, dose, route, time, notes }
 * @returns {Promise<{log, entry}>} the log + entry on success
 */
export async function logIntervention(entry) {
  // Find or create active log
  let log;
  try {
    const res = await axios.get(`${API}/intervention-logs?status=active`);
    log = res.data?.[0];
  } catch {
    log = null;
  }
  if (!log) {
    const res = await axios.post(`${API}/intervention-logs`, {
      patient_label: `Trip ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
    });
    log = res.data;
  }
  const payload = {
    type: entry.type || "intervention",
    name: entry.name || "",
    dose: entry.dose || "",
    route: entry.route || "",
    time: entry.time || nowTime(),
    notes: entry.notes || "",
  };
  const res = await axios.post(`${API}/intervention-logs/${log.id}/entries`, payload);
  return { log, entry: res.data };
}

/**
 * Convenience wrapper that wraps logIntervention with toast feedback.
 * On hard-failure (network down, server 5xx), the entry is queued in
 * localStorage and a "queued for retry" toast is shown so paramedics
 * are never left without feedback.
 *
 * @returns {Promise<{queued?: boolean, log?, entry?}>}
 */
export async function logInterventionWithToast(entry, successLabel) {
  try {
    const result = await logIntervention(entry);
    toast.success(successLabel || `${entry.name} logged`, {
      description: `Added to ${result.log.patient_label}`,
    });
    // Opportunistically flush any prior queued entries
    drainQueue().then(({ sent }) => {
      if (sent > 0) toast.success(`Synced ${sent} queued intervention${sent !== 1 ? "s" : ""}`);
    });
    return result;
  } catch (e) {
    const status = e?.response?.status;
    const isAuth = status === 401 || status === 403;
    const isNetwork = !e?.response; // request never reached server
    const payload = {
      type: entry.type || "intervention",
      name: entry.name || "",
      dose: entry.dose || "",
      route: entry.route || "",
      time: entry.time || nowTime(),
      notes: entry.notes || "",
    };

    if (isAuth) {
      toast.error("Session expired — please log in again", {
        description: `"${entry.name}" was saved offline and will sync after re-login.`,
        duration: 8000,
        action: {
          label: "Log in",
          onClick: () => { window.location.href = "/login"; },
        },
      });
      enqueue({ payload });
      throw e;
    }

    // Network or 5xx → queue + retry
    if (isNetwork || (status >= 500 && status < 600)) {
      enqueue({ payload });
      const total = loadQueue().length;
      toast.warning(`"${entry.name}" queued`, {
        description: `${isNetwork ? "Offline" : "Server hiccup"} — ${total} entr${total === 1 ? "y" : "ies"} will sync automatically.`,
        duration: 5000,
      });
      return { queued: true };
    }

    // Other errors (400, validation)
    toast.error(`Failed to log: ${entry.name}`, {
      description: e?.response?.data?.detail || "Tap again to retry.",
    });
    throw e;
  }
}

// Expose for diagnostics / manual sync trigger
export { drainQueue, loadQueue };
